import { useRef, useEffect, useCallback } from 'react'
import { useUser } from '../redux/reducer/user'
import { PROMISES_CONFIG } from '../constants/shared/preload/appPreload'
import { gamesList } from '../constants/copyright'
import useServiceData from './useServiceData'
import { useRequestsHandlerData } from './useRequestsHandlerData'
import { loadImages } from '../utils/img/img'
import { getTypeBySlotType } from '../utils/helpers/helpers'
import { importElfGameJS } from '../controllers/elf/items/import'
import { useApplicationCallbacks } from '../providers/application-callbacks'
import { isFunction } from 'lodash'

export const usePreloadPromises = ({
  setProgress = null,
  fulfilled = null,
  loadProportion = { promises: 80, controller: 20 },
}) => {
  const { profile } = useUser()
  const { load } = useServiceData()
  const callbacks = useApplicationCallbacks()

  const setProgressRef = useRef(setProgress)
  const loadProportionRef = useRef(loadProportion)
  const fulfilledRef = useRef(fulfilled)

  useEffect(() => {
    if (isFunction(setProgress)) {
      setProgressRef.current = setProgress
    } else {
      setProgressRef.current = null
    }
  }, [setProgress])

  useEffect(() => {
    loadProportionRef.current = loadProportion
  }, [loadProportion])

  useEffect(() => {
    fulfilledRef.current = fulfilled
  }, [fulfilled])

  const {
    getIndex,
    getParams,
    getFavoritesProducts,
    getShopItems,
    getQuests,
    getGameSettings,
    getLootBoxesList,
    // getYearStat,
    getIcons,
    getModals,
    // getGuessPriceTodayList,
    getWishes,
    getGameRating,
    getShopPrizes,
  } = callbacks

  const promisesArray = [
    getIcons,
    getParams,
    getWishes,
    getShopItems,
    getModals,
    // getGuessPriceTodayList,
    getFavoritesProducts,
    load,
    getLootBoxesList,
    // getYearStat,
    getShopPrizes,
  ]

  const parallelPromises = [
    getQuests,
    getGameSettings,
    ...Object.values(gamesList.list).reduce(
      (acc, { apiName, isRating }) =>
        !isRating ? acc : [...acc, () => getGameRating(apiName, { isSendGameRating: false })],
      [],
    ),
  ]

  const updateProgress = useCallback(() => {
    const currentSetProgress = setProgressRef.current
    const currentLoadProportion = loadProportionRef.current

    if (currentSetProgress && isFunction(currentSetProgress)) {
      currentSetProgress((prev) => {
        const increment = currentLoadProportion.promises / PROMISES_CONFIG.length
        const newProgress = Math.min(prev + increment, 100) // Ограничиваем максимум 100%
        return newProgress
      })
    }
  }, [])

  const preloadPromises = () => promisesArray.forEach((promise) => promise())

  const preloadParallelPromises = () => parallelPromises.forEach((promise) => promise())

  // Создаем конфигурации - они будут пересоздаваться при каждом рендере, но useRequestsHandlerData обработает это правильно
  const requestConfigs = PROMISES_CONFIG.map(({ address }, index, arr) => ({
    address,
    fulfilled: async ({ slots, shopItems }) => {
      if (!index) {
        loadImages(
          profile?.items.reduce(
            (acc, { image, is_equipped }) => (is_equipped && image ? [...acc, image] : acc),
            [],
          ),
        )

        preloadPromises()
      }

      updateProgress()

      if (index !== arr.length - 1) return

      const currentSetProgress = setProgressRef.current

      if (currentSetProgress && isFunction(currentSetProgress)) {
        currentSetProgress(100)
      }

      const data = await importElfGameJS()

      const getType = (arg) => {
        if (!Array.isArray(shopItems)) return

        const { slot } = shopItems.find(({ id }) => id === arg)
        const clothType = slots.find((itemSlot) => itemSlot.system_name === slot).type
        return getTypeBySlotType(clothType)
      }

      const selClothes = profile?.items.filter(
        ({ is_equipped, id }) => is_equipped && getType(id) === 'character',
      )

      await Promise.all(
        selClothes.map(({ id, slot, system_name, image, image_glb, image_gltf }) =>
          data?.wrapper?.putClothOn?.({ id, slot, system_name, image, image_glb, image_gltf }),
        ),
      )

      fulfilledRef.current?.()
    },
  }))

  useRequestsHandlerData(requestConfigs)

  return () => {
    getIndex()
    preloadParallelPromises()
  }
}
